import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, X } from "lucide-react";
import { getFlights } from "@/services";
import { Flight, City } from "@/types/flight";
import { FlightStatusBadge } from "./FlightStatusBadge";
import { formatTime, formatDate } from "@/lib/formatters";

interface CityFlightModalProps {
  isOpen: boolean;
  onClose: () => void;
  city: City | null;
}

export function CityFlightModal({ isOpen, onClose, city }: CityFlightModalProps) {
  const { data: allFlights = [], isLoading } = useQuery({
    queryKey: ['flights'],
    queryFn: () => getFlights(),
    enabled: isOpen && !!city,
  });

  const getDateRangeFlights = () => {
    if (!city || !allFlights.length) return { incoming: [], outgoing: [] };

    const now = new Date();
    
    // Incoming flights: last 2 days + next 5 days
    const incomingStartDate = new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000);
    const incomingEndDate = new Date(now.getTime() + 5 * 24 * 60 * 60 * 1000);
    
    // Outgoing flights: last 5 days
    const outgoingStartDate = new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000);
    const outgoingEndDate = now;

    const incoming = allFlights.filter(flight => {
      if (flight.arrival_city_id !== city.id) return false;
      const arrivalDate = new Date(flight.scheduled_arrival);
      return arrivalDate >= incomingStartDate && arrivalDate <= incomingEndDate;
    }).sort((a, b) => new Date(a.scheduled_arrival).getTime() - new Date(b.scheduled_arrival).getTime());

    const outgoing = allFlights.filter(flight => {
      if (flight.departure_city_id !== city.id) return false;
      const departureDate = new Date(flight.scheduled_departure);
      return departureDate >= outgoingStartDate && departureDate <= outgoingEndDate;
    }).sort((a, b) => new Date(a.scheduled_departure).getTime() - new Date(b.scheduled_departure).getTime());

    return { incoming, outgoing };
  };

  const { incoming, outgoing } = getDateRangeFlights();

  const groupFlightsByDate = (flights: Flight[], dateField: 'scheduled_departure' | 'scheduled_arrival') => {
    const grouped: { [key: string]: Flight[] } = {};
    
    flights.forEach(flight => {
      const date = formatDate(flight[dateField]);
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(flight);
    });
    
    return grouped;
  };

  const FlightList = ({ flights, title, dateField }: { 
    flights: Flight[], 
    title: string, 
    dateField: 'scheduled_departure' | 'scheduled_arrival' 
  }) => {
    const groupedFlights = groupFlightsByDate(flights, dateField);
    
    if (flights.length === 0) {
      return (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-foreground">No flights found for this period</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {Object.entries(groupedFlights).map(([date, dateFlights]) => (
          <Card key={date}>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{date}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dateFlights.map(flight => (
                  <div key={flight.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-semibold">{flight.flight_number}</span>
                        <FlightStatusBadge status={flight.status} />
                      </div>
                      <div className="text-sm text-foreground">
                        {flight.departure_city?.code} → {flight.arrival_city?.code}
                      </div>
                      <div className="text-sm">
                        {flight.departure_city?.name} → {flight.arrival_city?.name}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">
                        {dateField === 'scheduled_departure' 
                          ? formatTime(flight.scheduled_departure)
                          : formatTime(flight.scheduled_arrival)
                        }
                      </div>
                      <div className="text-sm text-foreground">
                        {flight.aircraft_type?.code}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-2xl">
                Flight Details for {city?.name}
              </DialogTitle>
              <DialogDescription>
                Airport Code: {city?.code}
              </DialogDescription>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Loading flight data...</span>
          </div>
        ) : (
          <Tabs defaultValue="incoming" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="incoming">
                Incoming Flights ({incoming.length})
              </TabsTrigger>
              <TabsTrigger value="outgoing">
                Outgoing Flights ({outgoing.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="incoming" className="mt-6">
              <div className="mb-4">
                <p className="text-sm text-foreground">
                  Flights arriving at {city?.name} (Last 2 days + Next 5 days)
                </p>
              </div>
              <FlightList 
                flights={incoming} 
                title="Incoming Flights" 
                dateField="scheduled_arrival"
              />
            </TabsContent>
            
            <TabsContent value="outgoing" className="mt-6">
              <div className="mb-4">
                <p className="text-sm text-foreground">
                  Flights departing from {city?.name} (Last 5 days)
                </p>
              </div>
              <FlightList 
                flights={outgoing} 
                title="Outgoing Flights" 
                dateField="scheduled_departure"
              />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}